import { useState } from 'react'
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet'
import { Drawer, DrawerContent, DrawerDescription, DrawerHeader, DrawerTitle } from '@/components/ui/drawer'
import { useIsMobile } from '@/hooks/useIsMobile'
import type { AccommodationDetail, LocationPriceReportRequest } from '@/api/accommodation/types'
import { trackPriceGuaranteeFormOpened, trackPriceGuaranteeSubmitted } from '@/lib/track'
import { PriceGuaranteeForm } from './PriceGuaranteeForm'

interface PriceGuaranteeSheetProps {
  accommodation: AccommodationDetail
  renderTrigger: (openSheet: () => void) => React.ReactNode
}

export function PriceGuaranteeSheet({ accommodation, renderTrigger }: PriceGuaranteeSheetProps) {
  const [open, setOpen] = useState(false)
  const isMobile = useIsMobile()

  const handleOpen = () => {
    trackPriceGuaranteeFormOpened(accommodation)
    setOpen(true)
  }

  const handleSubmitted = (report: LocationPriceReportRequest) => {
    trackPriceGuaranteeSubmitted(accommodation, {
      url: report.url,
      pricePerNight: report.pricePerNight,
    })
  }

  const title = 'Našli jste lepší cenu?'
  const description =
    'Pokud jste našli výhodnější nabídku tohoto ubytování, napište nám. Když to půjde, cenu dorovnáme.'

  const form = (
    <PriceGuaranteeForm accommodation={accommodation} onSent={handleSubmitted} onClose={() => setOpen(false)} />
  )

  if (isMobile) {
    return (
      <>
        {renderTrigger(handleOpen)}
        <Drawer open={open} onOpenChange={setOpen} repositionInputs={false}>
          <DrawerContent className="max-h-[90dvh]">
            <DrawerHeader>
              <DrawerTitle>{title}</DrawerTitle>
              <DrawerDescription>{description}</DrawerDescription>
            </DrawerHeader>
            <div className="min-h-0 flex-1 overflow-y-auto px-4 pb-6">{form}</div>
          </DrawerContent>
        </Drawer>
      </>
    )
  }

  return (
    <>
      {renderTrigger(handleOpen)}
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="right" className="flex w-full flex-col gap-0 sm:max-w-md">
          <SheetHeader className="border-b border-slate-100 p-6">
            <SheetTitle className="text-xl">{title}</SheetTitle>
            <SheetDescription>{description}</SheetDescription>
          </SheetHeader>
          <div className="min-h-0 flex-1 overflow-y-auto p-6">{form}</div>
        </SheetContent>
      </Sheet>
    </>
  )
}
